import { useEffect, useMemo, useState } from 'preact/hooks';
import { useSession } from '../../data/session';
import { getSupabase } from '../../lib/supabaseClient';
import { getSupabaseConfig } from '../../config';
import { requestPermissionAndSubscribe } from '../../push/subscribe';
import { savePushSubscription } from '../../push/save';
import type { SaveResult } from '../../push/save';
import { DeniedBanner } from '../install/DeniedBanner';
import { getMyInviteLinks } from '../../data/membership';
import { ShareRow } from '../membership/PartnerInvite';
import { bestEffortUnsubscribeAndDelete, signOutOnThisDevice } from '../../push/signOutDevice';

type PushState =
  | { kind: 'unsupported' }
  | { kind: 'denied' }
  | { kind: 'off' }
  | { kind: 'on'; endpoint: string }
  | { kind: 'loading' }
  | { kind: 'error'; message: string };

export function Settings(props: { navigate: (to: string) => void }) {
  const session = useSession();
  const [push, setPush] = useState<PushState>({ kind: 'loading' });
  const [links, setLinks] = useState<
    | { partnerUrl: string; neighborhoodUrl: string; isOwner: boolean }
    | null
  >(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  const origin = useMemo(() => (typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : ''), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Build invite links
      try {
        const data = await getMyInviteLinks();
        if (mounted && data) {
          setLinks({
            partnerUrl: `${origin}/family/${data.partner_invite_token}`,
            neighborhoodUrl: `${origin}/join/${data.neighborhood_invite_token}`,
            isOwner: !!data.is_owner,
          });
        }
      } catch {
        // ignore; keep null
      }
    })();
    return () => {
      mounted = false;
    };
  }, [origin]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (typeof window === 'undefined') return;
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        setPush({ kind: 'unsupported' });
        return;
      }
      if (Notification.permission === 'denied') {
        setPush({ kind: 'denied' });
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) {
          setPush({ kind: 'off' });
          return;
        }
        const endpoint = sub.endpoint;
        // Check current enabled flag for this endpoint
        const sb = getSupabase();
        if (!sb) {
          setPush({ kind: 'on', endpoint });
          return;
        }
        const { data, error } = await sb
          .from('push_subscriptions')
          .select('enabled')
          .eq('endpoint', endpoint)
          .maybeSingle();
        if (!mounted) return;
        if (error) {
          setPush({ kind: 'off' });
          return;
        }
        if (!data) {
          setPush({ kind: 'off' });
        } else if (data.enabled === false) {
          setPush({ kind: 'off' });
        } else {
          setPush({ kind: 'on', endpoint });
        }
      } catch {
        setPush({ kind: 'off' });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  async function toggleNotifications() {
    if (push.kind === 'loading') return;
    if (push.kind === 'off') {
      setPushError(null);
      // If browser already has a subscription, try to re-enable or re-save that endpoint
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          const sb = getSupabase();
          if (sb) {
            // Check if a row exists for this endpoint
            const { data: row, error: rowErr } = await sb
              .from('push_subscriptions')
              .select('enabled')
              .eq('endpoint', endpoint)
              .maybeSingle();
            if (rowErr) {
              setPush({ kind: 'off' });
              setPushError('Could not enable notifications. Please try again.');
              return;
            }
            if (row) {
              const { error: updErr } = await sb.from('push_subscriptions').update({ enabled: true }).eq('endpoint', endpoint);
              if (updErr) {
                setPush({ kind: 'off' });
                setPushError('Could not enable notifications. Please try again.');
                return;
              }
              setPush({ kind: 'on', endpoint });
              return;
            }
            // No row exists: save using the subscription's keys
            const json = sub.toJSON() as any;
            const p256dh = json?.keys?.p256dh ?? '';
            const auth = json?.keys?.auth ?? '';
            const { url, anonKey } = getSupabaseConfig();
            const accessToken = (await sb.auth.getSession()).data.session?.access_token ?? null;
            const user = (await sb.auth.getUser()).data.user ?? null;
            if (!user) {
              setPush({ kind: 'off' });
              setPushError('Could not enable notifications. Please try again.');
              return;
            }
            const saveRes: SaveResult = await savePushSubscription({
              supabaseUrl: url,
              anonKey,
              accessToken: accessToken ?? undefined,
              record: {
                profile_id: user.id,
                endpoint,
                p256dh,
                auth,
                user_agent: navigator.userAgent,
                enabled: true,
              },
            } as any);
            if (!(saveRes as any)?.ok) {
              setPush({ kind: 'off' });
              setPushError('Could not enable notifications. Please try again.');
              return;
            }
            setPush({ kind: 'on', endpoint });
            return;
          }
        }
      } catch {
        // fall through to permission flow
      }
      // No existing browser subscription: run enable flow
      const res = await requestPermissionAndSubscribe();
      if (res.kind === 'blocked') {
        setPush({ kind: 'denied' });
        return;
      }
      if (res.kind === 'error') {
        setPush({ kind: 'off' });
        setPushError('Could not enable notifications. Please try again.');
        return;
      }
      // Save to DB (require a signed-in user)
      const { url, anonKey } = getSupabaseConfig();
      const sb = getSupabase();
      const accessToken = sb ? (await sb.auth.getSession()).data.session?.access_token ?? null : null;
      const user = sb ? (await sb.auth.getUser()).data.user ?? null : null;
      if (!user) {
        setPush({ kind: 'off' });
        setPushError('Could not enable notifications. Please try again.');
        return;
      }
      const saveRes2: SaveResult = await savePushSubscription({
        supabaseUrl: url,
        anonKey,
        accessToken: accessToken ?? undefined,
        record: {
          profile_id: user.id,
          endpoint: res.endpoint,
          p256dh: res.p256dh,
          auth: res.auth,
          user_agent: navigator.userAgent,
          enabled: true,
        },
      } as any);
      if (!(saveRes2 as any)?.ok) {
        setPush({ kind: 'off' });
        setPushError('Could not enable notifications. Please try again.');
        return;
      }
      setPush({ kind: 'on', endpoint: res.endpoint });
      return;
    }
    // Turning off: update enabled=false for this endpoint only
    setPushError(null);
    let endpoint = push.kind === 'on' ? push.endpoint : null;
    if (!endpoint) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        endpoint = sub?.endpoint ?? null;
      } catch {
        endpoint = null;
      }
    }
    if (!endpoint) {
      setPushError('Could not turn off notifications. Please try again.');
      setPush({ kind: 'on', endpoint: push.kind === 'on' ? push.endpoint : '' });
      return;
    }
    try {
      const sb = getSupabase();
      if (sb) {
        const { error: updErr } = await sb.from('push_subscriptions').update({ enabled: false }).eq('endpoint', endpoint);
        if (updErr) {
          setPushError('Could not turn off notifications. Please try again.');
          setPush({ kind: 'on', endpoint });
          return;
        }
      }
    } catch {
      setPushError('Could not turn off notifications. Please try again.');
      setPush({ kind: 'on', endpoint });
      return;
    }
    setPush({ kind: 'off' });
  }

  async function sendTestNotification() {
    const sb = getSupabase();
    if (!sb) return;
    try {
      setTestMsg(null);
      setSendingTest(true);
      const { error } = await sb.functions.invoke('send-test-notification' as any, { body: {} as any });
      if (error) {
        setTestMsg("Couldn't send a test notification. Please try again.");
      } else {
        setTestMsg('Sent. It should arrive in a few seconds.');
      }
    } catch {
      setTestMsg("Couldn't send a test notification. Please try again.");
    } finally {
      setSendingTest(false);
    }
  }

  async function handleSignOut() {
    await signOutOnThisDevice();
    props.navigate('/sign-in');
  }

  async function handleLeaveNeighborhood() {
    setLeaveError(null);
    setLeaving(true);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Backend not configured');
      const { error } = await sb.rpc('leave_neighborhood' as any);
      if (error) throw error;
      await bestEffortUnsubscribeAndDelete();
      try {
        await sb.auth.signOut({ scope: 'local' } as any);
      } catch {}
      props.navigate('/');
    } catch (err: any) {
      setLeaveError(err?.message || 'Something went wrong. Please try again.');
      setLeaving(false);
    }
  }

  async function handleResetLink() {
    setResetError(null);
    setResetting(true);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Backend not configured');
      const { data, error } = await sb.rpc('reset_invite' as any);
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.invite_token) {
        const newUrl = `${origin}/join/${row.invite_token as string}`;
        setLinks((l) => (l ? { ...l, neighborhoodUrl: newUrl } : l));
      }
      setResetting(false);
      setConfirmReset(false);
    } catch (err: any) {
      setResetError(err?.message || 'Could not reset link. Please try again.');
      setResetting(false);
    }
  }

  return (
    <section class="install-safe" aria-label="Settings">
      <div class="install-card">
        <h2 class="install-title">Settings</h2>
        <div class="install-body">
          <div style="display:grid;gap:16px;">
            <div>
              <h3 style="margin:0 0 6px 0;">Notifications on this device</h3>
              {push.kind === 'denied' && <DeniedBanner />}
              {push.kind === 'unsupported' ? (
                <p class="muted" style="margin:0;">To get notifications, add Park Club to your Home Screen and open it from there.</p>
              ) : (
                <>
                  <p style="margin:0;">
                    Notifications are <strong>{push.kind === 'on' ? 'on' : 'off'}</strong> for this phone.
                  </p>
                  {pushError && <div class="error-text" role="alert" style="margin-top:6px;">{pushError}</div>}
                  {push.kind === 'on' && (
                    <div style="margin-top:8px;">
                      <button class="btn-secondary" type="button" onClick={() => void toggleNotifications()}>
                        Turn off
                      </button>
                    </div>
                  )}
                  {push.kind === 'off' && (
                    <div style="margin-top:8px;">
                      <button class="btn-secondary" type="button" onClick={() => void toggleNotifications()}>
                        Turn on
                      </button>
                    </div>
                  )}
                  {push.kind === 'loading' && (
                    <div style="margin-top:8px;">
                      <button class="btn-secondary" type="button" disabled>
                        Loading…
                      </button>
                    </div>
                  )}
                </>
              )}
              <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:8px;">
                <button class="btn-secondary" type="button" onClick={() => void sendTestNotification()} disabled={sendingTest}>
                  Send me a test notification
                </button>
                {testMsg && <div class="muted" role="status">{testMsg}</div>}
              </div>
            </div>

            <div>
              <h3 style="margin:0 0 6px 0;">Family</h3>
              <p class="muted" style="margin:0 0 8px 0;">{session.familyDisplayName}</p>
              {links && (
                <ShareRow title="Shores Park Club" text="Join our family on Shores Park Club" url={links.partnerUrl} />
              )}
            </div>

            <div>
              <h3 style="margin:0 0 6px 0;">Neighborhood invite link</h3>
              {links && <ShareRow title="Shores Park Club" text="Join our neighborhood on Shores Park Club" url={links.neighborhoodUrl} />}
              {links?.isOwner && !confirmReset && (
                <div style="margin-top:8px;">
                  <button class="btn-secondary" type="button" onClick={() => setConfirmReset(true)}>
                    Reset link
                  </button>
                </div>
              )}
              {links?.isOwner && confirmReset && (
                <div style="margin-top:8px;">
                  <div class="confirm-text">The old link will stop working. Anyone who already joined stays in.</div>
                  {resetError && <div class="error-text" role="alert">{resetError}</div>}
                  <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
                    <button class="btn-danger" type="button" onClick={() => void handleResetLink()} disabled={resetting}>
                      {resetting ? 'Resetting…' : 'Reset link'}
                    </button>
                    <button class="btn-secondary" type="button" onClick={() => setConfirmReset(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 style="margin:0 0 6px 0;">Account</h3>
              <div style="display:flex;gap:8px;flex-wrap:wrap;">
                <button class="btn-secondary" type="button" onClick={() => void handleSignOut()}>
                  Sign out
                </button>
                {!confirmLeave && (
                  <button class="btn-danger" type="button" onClick={() => setConfirmLeave(true)}>
                    Leave the neighborhood
                  </button>
                )}
              </div>
              {confirmLeave && (
                <div style="margin-top:8px;">
                  <div class="confirm-text">This deletes your account and data. This can't be undone.</div>
                  {leaveError && <div class="error-text" role="alert">{leaveError}</div>}
                  <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
                    <button class="btn-danger" type="button" onClick={() => void handleLeaveNeighborhood()} disabled={leaving}>
                      {leaving ? 'Leaving…' : 'Leave and delete'}
                    </button>
                    <button class="btn-secondary" type="button" onClick={() => setConfirmLeave(false)} disabled={leaving}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

